import { useEffect, useRef, useState } from 'react';
import { Route, Routes, useSearchParams } from "react-router-dom";
import { IConversation } from '@tiffin/core';
import { Loading } from '@tiffin/components';
import { ViewContext } from './Contexts';
import TifynLogo from "./assets/images/tifyn-logo.png"
import { auth, authenticateReducer, cartMultiReducer, conversationsHelper, conversationsMetricsReducer, globalConfigReducer, storeReducer, userReducer, Utils } from "@tiffin/app-common"

import { IonApp, setupIonicReact } from '@ionic/react';
import { useMediaQuery } from "react-responsive"
import { Modal } from 'antd';
import { TablePicker } from './components/home/TablePicker';

/* Redux */
import { useAppDispatch, useAppSelector } from "./hooks"

/* firebase */
import { User } from "firebase/auth"
import { Unsubscribe } from "@firebase/firestore"

/* Pages */
import { Home } from './pages/Home';
import { Cart } from './pages/Cart';
import { AccountDetails } from './pages/AccountDetails';
import { Messages } from './pages/Messages';
import { Orders } from './pages/Orders';
import { Login } from './pages/Login';
import { Checkout } from './pages/Checkout';
import { PostPayment } from './pages/PostPayment';
import { Error } from './pages/Error';

/* Components */
import { UnacknowledgedModal } from './components/account/UnacknowledgedModal';

import './App.css';

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css"

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css"
import "@ionic/react/css/structure.css"
import "@ionic/react/css/typography.css"

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css"
import "@ionic/react/css/float-elements.css"
import "@ionic/react/css/text-alignment.css"
import "@ionic/react/css/text-transformation.css"
import "@ionic/react/css/flex-utils.css"
import "@ionic/react/css/display.css"

function App() {

  setupIonicReact({
    mode: 'ios',
    swipeBackEnabled: false
  });

  const cartStatus = useAppSelector((state) => state.cartMulti.status)
  const partnerId = useAppSelector((state) => state.store.details?.id)
  const partnerName = useAppSelector((state) => state.store.details?.name)
  const partnerDescription = useAppSelector((state) => state.store.details?.description)
  const partnerLogoUrl = useAppSelector((state) => state.store.details?.logoImageUrl)
  const partnerStatus = useAppSelector((state) => state.store.status)
  const partnerUrlName = useAppSelector((state) => state.store.details?.urlName)
  const partnerNumOfTables = useAppSelector((state) => state.store.details?.numberOfTables)
  const partnerDineInEnabled = useAppSelector((state) => state.store.details?.offerDineIn)

  const globalConfig = useAppSelector((state) => state.globalConfig)
  const user = useAppSelector((state) => state.user)
  const authenticate = useAppSelector((state) => state.authenticate)

  const [name, setName] = useState<string>();
  const [showError, setShowError] = useState(false);
  
  const dispatch = useAppDispatch()
  const unsubUnreadConverations = useRef<Unsubscribe | null>(null)

  const mobileView = useMediaQuery({ query: '(max-width:768px)' })
  const [searchParams] = useSearchParams();
  const dineInTable = searchParams.get('table');
  
  useEffect(() => {
    let vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }, [])

  //Get the latest config
  useEffect(() => {
    dispatch(globalConfigReducer.getConfig())
  }, [dispatch])

  useEffect(() => {
    //Extract name from path on load
    const regex = /\/(.*?)(\/|$)/g;
    const partnerName = window.location.pathname.match(regex)?.[0].replaceAll("/", "");
    if(partnerName) {
      setName(Utils.convertToLowerName(decodeURIComponent(partnerName)));
    } else {
      setShowError(true);
    }
  }, [])

  useEffect(() => {
    //Fetch the store regardless of signed in or not
    if(name) {
      dispatch(storeReducer.getStore(name))
    }

  }, [dispatch, name])

  useEffect(() => {
    const unsubcribe = auth.onAuthStateChanged((user: User | null) => {
      if(partnerStatus === "succeeded") {
        //Only fetch user and cart once user is confirmed sign in
        if(user) {
          dispatch(userReducer.fetchUser())
        } else {
          dispatch(authenticateReducer.setUnauthenticated())
        }
      }
    })

    return () => {
      unsubcribe()
    }
  }, [dispatch, partnerStatus])

  useEffect(() => {
    if(authenticate.status === "authenticated" || authenticate.status === "unauthenticated") {
      dispatch(cartMultiReducer.fetchCart({tableNumber: dineInTable ? parseInt(dineInTable) : undefined}))
    }
  }, [dineInTable, authenticate.status, dispatch])

  useEffect(() => {
    //Cannot proceed without global config
    if(!globalConfig) return

    if(user.status === "succeeded") {
      const policyAckVersion = globalConfig.tcCustomerVersion;
      if(user.firstName && user.lastName && user.phone && user.phonePrefix && user.email && user.policyAckVer && user.policyAckVer === policyAckVersion) {
        dispatch(authenticateReducer.setAuthenticated())
      } else if(user.firstName && user.lastName && user.phone && user.phonePrefix && user.email && user.policyAckVer && user.policyAckVer !== policyAckVersion) {
        dispatch(authenticateReducer.setUnacknowledged())
      } else {
        dispatch(authenticateReducer.setIncomplete())
      }
    } else if(user.status === "idle") {
      dispatch(authenticateReducer.setIncomplete())
    }
  }, [user, globalConfig, dispatch])

  useEffect(() => {
    if(auth.currentUser && user.status === "succeeded" && partnerId) {
      //Subscribe to conversations metrics
      unsubUnreadConverations.current = conversationsHelper.subscribeToSingleConversationInfo(partnerId, user, (conversation: IConversation) => {
        dispatch(conversationsMetricsReducer.updateMetrics({
          customerUnread: conversation.customerUnreadMessages,
          tiffinUnread: 0
        }))
      })
    }

    return () => {
      if(unsubUnreadConverations.current !== null) {
        unsubUnreadConverations.current()
      }
    }
  })

  //Update the title
  useEffect(() => {
    if(partnerName) {
      if(partnerDescription) {
        document.title = `${partnerName} | ${partnerDescription}`
      } else {
        document.title = partnerName
      }
    }
  }, [partnerName, partnerDescription])

  useEffect(() => {
    var link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
    if (link && partnerStatus === "succeeded") {
      if(partnerLogoUrl) {
        link.href = partnerLogoUrl;
      } else {
        link.href = "./favicon.ico" 
      }
    }
  }, [partnerLogoUrl, partnerStatus])

  useEffect(() => {
    if(!partnerDineInEnabled && dineInTable && partnerUrlName) {
      //Since dine in is not offered, redirect to main page
      window.location.href = `/${partnerUrlName}`
    }
  }, [partnerDineInEnabled, dineInTable])

  return (
    <ViewContext.Provider value={mobileView ? 'mobile' : 'web'}>
      <IonApp className="App">
        {
          (name && authenticate.status !== "unknown" && partnerStatus === "succeeded") &&
          <Routes>
            <Route path={`/${name}`} element={<Home />} />
            <Route path={`/${name}/cart`} element={<Cart />} />
            <Route path={`/${name}/checkout/:date`} element={<Checkout />} />
            <Route path={`/${name}/postPayment/:date`} element={<PostPayment />} />
            <Route path={`/${name}/orders`} element={<Orders />} />
            <Route path={`/${name}/account`} element={<AccountDetails />} />
            <Route path={`/${name}/messages`} element={<Messages />} />
            <Route path={`/${name}/signin`} element={<Login />} />
          </Routes>
        }
        {
          (showError || partnerStatus === "failed") &&
          <Routes>
            <Route path={`/${name}`} element={<Error />} />
          </Routes>
        }
        <Loading isOpen={cartStatus === "loading"} />
        {
          globalConfig.tcCustomerVersion &&
            <UnacknowledgedModal 
            open={authenticate.status === "unacknowledged"}
            tcCustomerVersion={globalConfig.tcCustomerVersion}
          />
        }
        <Loading 
          isOpen={!showError && partnerStatus !== "failed" && (authenticate.status === "unknown" || partnerStatus === "loading")} 
          showIcon={true} 
          iconOptions={{
            alt: "Tifyn Logo",
            src: TifynLogo
          }} 
        />
        <Modal
          title="Please select a table"
          open={dineInTable && partnerNumOfTables ? parseInt(dineInTable) > partnerNumOfTables : false}
          closable={false}
          footer={[]}
        >
          <TablePicker error="The table you are trying to order from does not exist, please select the correct table" />
        </Modal>
        <Modal
          title="Unable to place dine in orders"
          open={!!dineInTable && partnerStatus === "succeeded" && partnerNumOfTables === undefined}
          closable={false}
          footer={[]}
        >
          This store currently has not set up dine in orders. Please contact staff for assistance.
        </Modal>
      </IonApp>
    </ViewContext.Provider>
  );
}

export default App;
